import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setAuthTokenProvider } from "@repo/api-client";
import { decodeJwtClaims, decodeJwtPayload } from "./claims";
import { AuthStateContext } from "./context";
import type { AuthState, Identity } from "./context";

/**
 * Dev-only stub auth (`VITE_AUTH_MODE=stub`) — the frontend half of the mock-auth escape hatch.
 *
 * Instead of the OIDC redirect dance it mints an HS256 token from the backend's `local-auth`
 * profile (`POST ${VITE_API_URL}/internal/local-auth/token`, role from `VITE_AUTH_STUB_ROLE`,
 * default "staff"), wires it into @repo/api-client's token provider, and populates the SAME
 * [AuthStateContext] the OIDC path does — so `useAuth`/`RequireAuth` and every consuming app work
 * unchanged. Re-mints at half the token lifetime so a long dev session never expires.
 *
 * Fail-closed: `readAuthMode()` throws on `stub` in a production build, and the backend endpoint
 * only exists under the `local-auth` Spring profile (404 everywhere else).
 */

interface MintedToken {
  accessToken: string;
  expiresIn: number;
}

async function mintToken(): Promise<MintedToken> {
  const env = import.meta.env;
  const apiUrl = env.VITE_API_URL ?? "";
  const role = env.VITE_AUTH_STUB_ROLE ?? "staff";
  const response = await fetch(`${apiUrl}/internal/local-auth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) {
    throw new Error(
      `local-auth mint failed (HTTP ${response.status}) — is the backend running with the local-auth profile?`,
    );
  }
  const body = (await response.json()) as { access_token: string; expires_in: number };
  return { accessToken: body.access_token, expiresIn: body.expires_in };
}

function toIdentity(token: string): Identity | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    sub: String(payload.sub ?? ""),
    email: typeof payload.email === "string" ? payload.email : undefined,
    name: typeof payload.name === "string" ? payload.name : undefined,
  };
}

export interface StubAuthProviderProps {
  children: React.ReactNode;
}

export function StubAuthProvider({ children }: StubAuthProviderProps) {
  const [token, setToken] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  // Ref mirror so the token provider handed to @repo/api-client always reads the latest token
  // without re-registering on every renewal.
  const tokenRef = useRef<string | undefined>(undefined);
  const renewTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const applyToken = useCallback((next: string | undefined) => {
    tokenRef.current = next;
    setToken(next);
  }, []);

  const clearRenewTimer = useCallback(() => {
    if (renewTimerRef.current !== undefined) {
      clearInterval(renewTimerRef.current);
      renewTimerRef.current = undefined;
    }
  }, []);

  /** Mint now, then keep re-minting at half the token lifetime. */
  const mintAndSchedule = useCallback(async () => {
    setIsLoading(true);
    try {
      const minted = await mintToken();
      applyToken(minted.accessToken);
      clearRenewTimer();
      renewTimerRef.current = setInterval(
        () => {
          mintToken()
            .then((renewed) => applyToken(renewed.accessToken))
            .catch((error: unknown) =>
              // oxlint-disable-next-line no-console -- dev-only stub (fail-closed in prod, see file docblock); surfacing renewal failures to the console is the intended UX here.
              console.error("[@repo/auth stub] token renewal failed", error),
            );
        },
        (minted.expiresIn / 2) * 1000,
      );
    } catch (error) {
      // oxlint-disable-next-line no-console -- dev-only stub (fail-closed in prod, see file docblock); surfacing mint failures to the console is the intended UX here.
      console.error("[@repo/auth stub] token mint failed", error);
      applyToken(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [applyToken, clearRenewTimer]);

  useEffect(() => {
    setAuthTokenProvider(() => tokenRef.current);
    // Deferred a microtask so `mintAndSchedule`'s own synchronous `setIsLoading(true)` (its first line)
    // doesn't run inside this effect's own commit-phase call stack (react(set-state-in-effect)) — the
    // token-provider registration above still runs synchronously in this effect, which is the ordering
    // guarantee that matters (see the module comment).
    queueMicrotask(() => void mintAndSchedule());
    return clearRenewTimer;
  }, [mintAndSchedule, clearRenewTimer]);

  const state = useMemo<AuthState>(
    () => ({
      isAuthenticated: token !== undefined,
      isLoading,
      user: token !== undefined ? toIdentity(token) : null,
      claims: decodeJwtClaims(token),
      // `idpHint` is meaningless here — the stub mints a token directly instead of visiting an
      // issuer, so there is no chooser to skip. Accepted and ignored so a screen written against
      // the real flow (the Google signup path) still runs under VITE_AUTH_MODE=stub.
      login: () => void mintAndSchedule(),
      logout: () => {
        clearRenewTimer();
        applyToken(undefined);
      },
      getToken: () => tokenRef.current,
      // Re-mints from the local-auth endpoint. The backend rebuilds the token from the CURRENT
      // database state, so claims written since the last mint (a freshly provisioned tenant_id)
      // appear here too — the stub mirrors the real flow rather than pretending to refresh.
      refreshClaims: () => mintAndSchedule(),
    }),
    [token, isLoading, mintAndSchedule, clearRenewTimer, applyToken],
  );

  return <AuthStateContext.Provider value={state}>{children}</AuthStateContext.Provider>;
}
